import { Button } from '@mui/material';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { useIsSupplier } from 'shared/permissions/useIsSupplier';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTechnicianCoordinator } from 'shared/permissions/useIsTechnicianCoordinator';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { CircularProgressWithLabel } from 'components/ui/data-display/CircularProgressWithLabel';
import {
  ITransportDocument,
  ITransportDocumentLine
} from 'features/inventory/transport-documents/types';
import { forEach } from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { printLabel } from 'features/zebra';
import toast from 'shared/utils/toast';
import { DdtFinancialsCard } from '../card/DdtFinancialsCard';
import { transportDocumentLineService } from '../../api/transport-document-line.service';
import { transportDocumentService } from '../../api/transport-document.service';
import { EnhancedDialog } from 'components/enhanced';
import { TransportDocumentLinesColumns } from '../../transport-document-lines';

interface MovementsTabProps {
  ddt: ITransportDocument;
  getDDTById: any;
}

export function MovementsTab({ ddt, getDDTById }: MovementsTabProps) {
  const { t } = useTranslation();
  const prefix = 'pages.ddt.detail';
  const datagridRefresh = useRef(null);
  const rowsRef = useRef<ITransportDocumentLine[]>([]);

  const isTenantAdmin = useIsTenantAdministrator();
  const isDisableEdit =
    useIsTechnicianCoordinator() || useIsTechnician() || useIsSupplier() || useIsDirector();

  const [openPrintLabelDialog, setOpenPrintLabelDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [printBarcodes, setPrintBarcodes] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);

  const handleRetreiveDetailsToPrint = () => {
    // ok iniziamno stampando la lista di movimenti
    // transportDocumentLineService.getBulkPrintLables(ddt.id).then((res) => {
    //   if (!res) toast.error(t(prefix + '.error-print'));
    //   else {
    //     setLabelsToPrint(res);
    //   }
    // });

    handleOpenPrintLabelDialog();
    setProgress(totalQuantity);
  };

  const handleCheckBarcode = (id: number) => {
    setPrintBarcodes([...printBarcodes, id]);
  };

  const handleUncheckBarcode = (id: number) => {
    setPrintBarcodes(printBarcodes.filter((b) => b !== id));
  };

  const handleOnCloseUpdateDDT = () => {
    if (datagridRefresh.current) datagridRefresh.current();
  };

  const handleOnUpdateExcluded = (lineID, excluded_params) => {
    transportDocumentLineService.update(lineID, { excluded_movement: excluded_params }).then(() => {
      if (datagridRefresh.current) datagridRefresh.current();
      toast.success(t(`${prefix}.excluded-line-updated`));
    });
  };
  const handleRecalculateDDTAmount = () => {
    transportDocumentService.recalculateAmount(ddt.id).then(() => {
      getDDTById();
    });
  };

  const handleOpenPrintLabelDialog = () => {
    setOpenPrintLabelDialog(true);
    setProgress(0);
  };

  const handleClosePrintLabelDialog = () => {
    setOpenPrintLabelDialog(false);
    setProgress(0);
  };

  const handlePrintAllMovements = (printAll = false) => {
    // conferma per stampare tutti i movimenti
    setIsLoading(true);
    forEach(rowsRef.current, (ddtLine) => {
      for (let i = 0; i < (printAll ? ddtLine.quantity : 1); i++) {
        printLabel(
          ddtLine.product,
          ddtLine.product_batch,
          ddtLine.expiration_date,
          ddtLine.description,
          printBarcodes.includes(ddtLine.id)
        );
      }
      setProgress(progress + ddtLine.quantity);
    });
    setIsLoading(false);
    handleClosePrintLabelDialog();
  };

  const totalQuantity = rowsRef.current.reduce((sum, labelDetail) => {
    return sum + labelDetail.quantity;
  }, 0);

  const columns = TransportDocumentLinesColumns(
    false, // locked
    true, // headerInfo
    handleOnCloseUpdateDDT, // onCloseUpdate
    handleCheckBarcode, // handleCheck
    handleUncheckBarcode, // handleUncheck
    handleOnUpdateExcluded, // handleUpdateExcluded
    isDisableEdit // disableEdit
  );
  return (
    <>
      {isTenantAdmin && (
        <DdtFinancialsCard
          amount={ddt.amount}
          expectedAmount={ddt.expected_amount}
          onRecalculate={handleRecalculateDDTAmount}
        />
      )}
      <EnhancedCard
        title={t(`${prefix}.movement-list`)}
        buttons={
          <Button onClick={() => handleRetreiveDetailsToPrint()} variant="contained">
            {t(`${prefix}.print-all`)}
          </Button>
        }>
        <EnhancedDataGrid
          columns={columns}
          service={transportDocumentLineService}
          outerParams={{ document_id: ddt?.id }}
          rows={rowsRef}
          refresh={datagridRefresh}
        />
      </EnhancedCard>
      {/* Print Label */}
      <EnhancedDialog
        open={openPrintLabelDialog}
        onClose={() => handleClosePrintLabelDialog()}
        title={t(`${prefix}.confirmation-print`)}
        costumFooter={
          <>
            <Button color="error" onClick={() => handleClosePrintLabelDialog()}>
              {t('actions.cancel')}
            </Button>
            <Button onClick={() => handlePrintAllMovements()}>{t(`${prefix}.single-print`)}</Button>
            <Button variant="contained" onClick={() => handlePrintAllMovements(true)}>
              {t(`${prefix}.all-print`, { quantity: totalQuantity })}
            </Button>
          </>
        }>
        {!isLoading ? (
          <div> {t(`${prefix}.confirmation-description-text`, { quantity: totalQuantity })}</div>
        ) : (
          <div>
            {t(`${prefix}.print-in-progress`)}
            <br />
            <CircularProgressWithLabel value={progress} />
          </div>
        )}
      </EnhancedDialog>
    </>
  );
}

export default MovementsTab;
